/**
 * Whether the operator has tucked the nav rail away.
 *
 * A per-browser convenience and nothing more: the depot laptop and the tablet
 * beside a charger may reasonably disagree, and losing the choice in a private
 * window costs one click. So storage failures — blocked site data, a private
 * window, an unexpected value — all quietly mean "rail shown", which is the
 * state that never hides a destination from someone who did not ask.
 */

/** The two calls this needs from `Storage`, so a test can hand in a fake. */
export interface PreferenceStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEY = "ocpp-admin.rail-hidden";

export function readRailHidden(store: PreferenceStore | undefined): boolean {
  try {
    return store?.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function writeRailHidden(store: PreferenceStore | undefined, hidden: boolean): void {
  try {
    store?.setItem(KEY, hidden ? "1" : "0");
  } catch {
    // Not persisted; the in-memory state still applies for this page load.
  }
}

const GROUPS_KEY = "ocpp-admin.rail-collapsed-groups";

/**
 * The nav groups the operator has folded shut, by caption. Anything unreadable
 * means none: an open group costs a few lines, a wrongly shut one hides a page.
 */
export function readCollapsedGroups(store: PreferenceStore | undefined): readonly string[] {
  try {
    const parsed: unknown = JSON.parse(store?.getItem(GROUPS_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
}

export function writeCollapsedGroups(
  store: PreferenceStore | undefined,
  groups: readonly string[],
): void {
  try {
    store?.setItem(GROUPS_KEY, JSON.stringify(groups));
  } catch {
    // Not persisted; the in-memory state still applies for this page load.
  }
}

/** A new list with `group` folded if it was open, opened if it was folded. */
export function toggleGroup(collapsed: readonly string[], group: string): readonly string[] {
  return collapsed.includes(group)
    ? collapsed.filter((entry) => entry !== group)
    : [...collapsed, group];
}

/** `window.localStorage`, or nothing where even touching it throws. */
export function browserStore(): PreferenceStore | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}
